import React from 'react';
import { View } from 'react-native-web';
import { colors, typography } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';

// Shared shell for the public marketing pages McKayla asked for on 2026-08-20.
// She sanctioned template pages explicitly on the call: "can you just put the
// template page on so that then when I'm on the site, I can say, okay, now I
// can imagine what I want this to show."
//
// ⛔ THE BANNER IS LOAD-BEARING — do not remove it to make a page look finished.
// themoneypersonality.com is production and Nina has live clients on it. A
// placeholder page with no marker is indistinguishable from a real page with
// bad copy, and a visitor cannot tell which they are reading. Every page in
// this folder carries the banner until real copy replaces it, at which point
// the page stops using this shell.
//
// The copy on these pages is McKayla's to write (plan Q4). Section headings
// here are taken verbatim from what she named in the videos; the body text
// says what the section is for and does not invent marketing claims.

export function PlaceholderBanner() {
  return (
    <View style={styles.banner}>
      <span style={styles.bannerText}>
        Preview — this page is a layout template. The wording is a placeholder, not final copy.
      </span>
    </View>
  );
}

export default function TemplatePage({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children?: React.ReactNode;
}) {
  const { isMobile } = useResponsive();

  return (
    <View style={styles.page}>
      <View style={styles.inner}>
        <PlaceholderBanner />
        <h1
          style={{
            margin: 0,
            marginTop: 32,
            fontFamily: typography.fontFamily.body,
            fontWeight: 700,
            fontSize: isMobile ? 36 : 56,
            color: colors.dark,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            marginTop: 16,
            marginBottom: 0,
            fontFamily: typography.fontFamily.body,
            fontSize: isMobile ? 16 : 18,
            lineHeight: 1.6,
            color: colors.gray[700],
            maxWidth: 720,
          }}
        >
          {intro}
        </p>
        {children}
      </View>
    </View>
  );
}

export function TemplateSection({
  heading,
  body,
}: {
  heading: string;
  body: string;
}) {
  const { isMobile } = useResponsive();
  return (
    <View style={styles.section}>
      <h2
        style={{
          margin: 0,
          fontFamily: typography.fontFamily.body,
          fontWeight: 700,
          fontSize: isMobile ? 20 : 24,
          color: colors.dark,
        }}
      >
        {heading}
      </h2>
      <p
        style={{
          marginTop: 8,
          marginBottom: 0,
          fontFamily: typography.fontFamily.body,
          fontSize: 16,
          lineHeight: 1.6,
          color: colors.gray[600],
        }}
      >
        {body}
      </p>
    </View>
  );
}

const styles = {
  page: {
    flex: 1,
    backgroundColor: colors.white,
    minHeight: '100vh',
    width: '100%',
    alignItems: 'center',
  } as React.CSSProperties,
  inner: {
    width: '100%',
    maxWidth: 960,
    // MainHeader is fixed-position and ~145px tall. At 96 the preview banner
    // rendered UNDERNEATH it and was clipped — verified in a browser, not inferred.
    paddingTop: 176,
    paddingBottom: 96,
    paddingLeft: 24,
    paddingRight: 24,
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  banner: {
    backgroundColor: '#FFF4CE',
    border: '1px solid #E6D08A',
    borderRadius: 8,
    padding: 12,
    alignSelf: 'flex-start',
  } as React.CSSProperties,
  bannerText: {
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    color: '#6B5400',
  } as React.CSSProperties,
  section: {
    marginTop: 40,
    paddingTop: 24,
    borderTop: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,
} as const;
